import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Empty, Spin } from "antd";
import {
  CheckOutlined,
  FileDoneOutlined,
  MinusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import { useUninvoicedBills } from "../../../../../hooks/useUninvoicedBills";
import { isCashNoReceiptBill } from "../../../../../lib/billFiscalization";
import { useUsers } from "../../../../../hooks/useUsers";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";

interface Props {
  open: boolean;
  onClose: () => void;
  // Подаваме пълните BillHistory обекти — InvoiceCreateModal ползва items,
  // суми, ДДС и пр., вместо да fetch-ва отново по id.
  onConfirm: (bills: BillHistory[]) => void;
}

const formatTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("bg-BG", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatMoney = (v: number | string): string => {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

export const InvoiceBillSelectionModal = ({
  open,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: fetched = [], isLoading, isError } = useUninvoicedBills({
    enabled: open,
  });
  // Закритите в брой без бон („Закриване на сметка(1)") нямат фискален
  // документ, срещу който да се издаде фактура — не ги предлагаме изобщо.
  const bills = useMemo(
    () => fetched.filter((b) => !isCashNoReceiptBill(b)),
    [fetched],
  );
  const { data: users = [] } = useUsers();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  // Map waiter_id → display name (имена, fallback username, fallback id).
  const waiterName = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((u) => {
      map.set(u.id, u.names || u.username);
    });
    return (id: number | null): string => {
      if (id == null) return "—";
      return map.get(id) ?? `#${id}`;
    };
  }, [users]);

  const filtered = useMemo(() => {
    if (!search.trim()) return bills;
    const q = search.trim().toLowerCase();
    return bills.filter((b) => {
      const wn = waiterName(b.waiter_id).toLowerCase();
      return (
        String(b.id).includes(q) ||
        b.name.toLowerCase().includes(q) ||
        (b.payment_method_name?.toLowerCase().includes(q) ?? false) ||
        wn.includes(q)
      );
    });
  }, [bills, search, waiterName]);

  const allSelected =
    filtered.length > 0 && filtered.every((b) => selected.has(b.id));
  const someSelected = filtered.some((b) => selected.has(b.id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        filtered.forEach((b) => next.delete(b.id));
      } else {
        filtered.forEach((b) => next.add(b.id));
      }
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    const picked = bills.filter((b) => selected.has(b.id));
    onConfirm(picked);
  };

  // Клавиатурно поведение в стила на ClientsModal:
  // - Esc в search → чисти + blur. Esc извън search → затваря модала.
  // - Enter извън search → потвърждава.
  // - Произволна печатна клавиша на desktop, докато focus-ът не е в input →
  //   автоматично почва да пише в search-а и го фокусира (без click).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (e.target instanceof HTMLInputElement) {
          setSearch("");
          (e.target as HTMLElement).blur?.();
          return;
        }
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Enter" && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        handleConfirm();
        return;
      }
      // Auto-typing: само на desktop и само ако focus-ът не е вече в input.
      if (!isDesktop) return;
      if (e.target instanceof HTMLInputElement) return;
      if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        setSearch((prev) => prev + e.key);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () =>
      window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose, selected, isDesktop]);

  const modalWidth = isMobile ? undefined : isTablet ? 720 : 960;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="inv-modal"
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "88vh",
        }}
      >
        <style>{`
          .inv-modal .row { transition: background 120ms; }
          .inv-modal .row:hover { background: ${R.brandTint}; }
          .inv-modal .row.is-selected { background: ${R.brandTint}; }
          .inv-modal input[type="text"] {
            width: 100%;
            height: 40px;
            padding: 4px 12px 4px 36px;
            background: ${R.surface};
            border: 1px solid ${R.border};
            border-radius: 10px;
            font-size: 13px;
            color: ${R.ink};
            outline: none;
            transition: border-color 120ms, box-shadow 120ms;
          }
          .inv-modal input[type="text"]:focus {
            border-color: ${R.brand};
            box-shadow: 0 0 0 3px rgba(217,119,6,0.12);
          }
          .inv-modal input[type="text"]::placeholder {
            color: ${R.textMuted};
          }
        `}</style>

        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoicing.selectBillTitle")}
          subtitle={t("invoicing.selectBillSubtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {/* Search row */}
        <div
          style={{
            padding: isNarrow ? "12px 16px 8px" : "14px 24px 10px",
            background: R.surface,
          }}
        >
          <div style={{ position: "relative" }}>
            <SearchOutlined
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                color: R.textMuted,
                fontSize: 14,
                pointerEvents: "none",
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t("invoicing.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Карта с таблицата — с лява/дясна padding, равна на search-а */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: isNarrow ? "0 16px 12px" : "0 24px 14px",
            background: R.surface,
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${R.border}`,
              borderRadius: 10,
              overflow: "hidden",
              background: R.surface,
            }}
          >
            {/* Table header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow
                  ? "32px 56px 1fr 1fr 90px"
                  : "32px 70px 1fr 1.2fr 1.2fr 110px",
                gap: 10,
                padding: isNarrow ? "8px 12px" : "10px 14px",
                background: R.bg,
                borderBottom: `1px solid ${R.border}`,
                fontSize: 10.5,
                fontWeight: 700,
                color: R.textMuted,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                alignItems: "center",
              }}
            >
              <CheckIndicator
                state={
                  allSelected
                    ? "checked"
                    : someSelected
                      ? "indeterminate"
                      : "unchecked"
                }
                onClick={toggleAll}
                ariaLabel={t("invoicing.selectAll")}
              />
              <span>{t("invoicing.colId")}</span>
              <span>{t("invoicing.colTable")}</span>
              {!isNarrow && <span>{t("invoicing.colWaiter")}</span>}
              <span>{t("invoicing.colPayment")}</span>
              <span style={{ textAlign: "right" }}>
                {t("invoicing.colTotal")}
              </span>
            </div>

            {/* Scrollable body */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                background: R.surface,
              }}
            >
          {isLoading ? (
            <div
              style={{
                padding: 48,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Spin />
            </div>
          ) : isError ? (
            <div style={{ padding: 48, textAlign: "center", color: R.danger }}>
              {t("invoicing.loadError")}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32 }}>
              <Empty
                description={
                  <span style={{ color: R.textSoft, fontSize: 13 }}>
                    {bills.length === 0
                      ? t("invoicing.emptyAll")
                      : t("invoicing.emptyFiltered")}
                  </span>
                }
              />
            </div>
          ) : (
            filtered.map((b) => {
              const isSel = selected.has(b.id);
              return (
                <div
                  key={b.id}
                  className={`row${isSel ? " is-selected" : ""}`}
                  role="button"
                  onClick={() => toggleOne(b.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isNarrow
                      ? "32px 56px 1fr 1fr 90px"
                      : "32px 70px 1fr 1.2fr 1.2fr 110px",
                    gap: 10,
                    padding: isNarrow ? "10px 12px" : "11px 14px",
                    borderBottom: `1px solid ${R.border}`,
                    alignItems: "center",
                    cursor: "pointer",
                    background: isSel ? R.brandTint : R.surface,
                    fontSize: 13,
                    color: R.ink,
                  }}
                >
                  <CheckIndicator
                    state={isSel ? "checked" : "unchecked"}
                    onClick={(e) => {
                      e?.stopPropagation();
                      toggleOne(b.id);
                    }}
                    ariaLabel={`${t("invoicing.selectRow")} ${b.name}`}
                  />
                  <span
                    style={{
                      fontFamily: R.mono,
                      fontSize: 12.5,
                      color: R.textSoft,
                    }}
                  >
                    {formatTime(b.closed_at)}
                  </span>
                  <span style={{ fontWeight: 600 }}>{b.name}</span>
                  {!isNarrow && (
                    <span style={{ color: R.textSoft, fontSize: 12.5 }}>
                      {waiterName(b.waiter_id)}
                    </span>
                  )}
                  <span style={{ color: R.textSoft, fontSize: 12.5 }}>
                    {b.payment_method_name ?? "—"}
                  </span>
                  <span
                    style={{
                      textAlign: "right",
                      fontFamily: R.mono,
                      fontWeight: 600,
                      color: R.ink,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatMoney(b.total_amount)}
                  </span>
                </div>
              );
            })
          )}
            </div>
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={
              selected.size > 0
                ? t("invoicing.confirmWithCount", { count: selected.size })
                : t("invoicing.confirm")
            }
            onClick={handleConfirm}
            disabled={selected.size === 0}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

// Кръгъл „checkbox" в стила на CloseTableModal payment-method селектора —
// 20×20, brand-filled когато е избран, dash за indeterminate.
function CheckIndicator({
  state,
  onClick,
  ariaLabel,
}: {
  state: "checked" | "unchecked" | "indeterminate";
  onClick: (e?: React.MouseEvent) => void;
  ariaLabel: string;
}) {
  const filled = state === "checked" || state === "indeterminate";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-checked={state === "checked"}
      role="checkbox"
      style={{
        width: 20,
        height: 20,
        borderRadius: 10,
        padding: 0,
        background: filled ? R.brand : R.surface,
        border: `1px solid ${filled ? R.brand : R.borderMed}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        cursor: "pointer",
        flexShrink: 0,
        transition: "background 120ms, border-color 120ms",
      }}
    >
      {state === "checked" && <CheckOutlined style={{ fontSize: 11 }} />}
      {state === "indeterminate" && (
        <MinusOutlined style={{ fontSize: 11 }} />
      )}
    </button>
  );
}

export default InvoiceBillSelectionModal;
